/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import * as moment from "moment";
import { debounce, transform } from "lodash";
import { DropdownItemProps, Grid, Form, Button, Icon } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import {
    Calendar as BaseCalendar,
    CalendarDetails,
    TimePeriod,
    Appointment,
    CalendarView,
    isNonSlotClinicAvailable,
    GapDurations
} from "@teamprevent/common/calendar";
import { toast } from "@teamprevent/common/toasts";
import { debounceDelay } from "@teamprevent/common/filtering/constants";
import { getProfessionDropdownOptions } from "@teamprevent/common/adminUser/models";
import { filterAppointsBasedOnGapLength } from "@teamprevent/common/calendar/models";
import { ClinicLocationTypeName } from "@teamprevent/common/appointment/models";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";

import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import {
    CalendarDetails as AdminCalendarDetails,
    emptyCalendarDetails,
    getDateOptionsForLocumUser,
    ClinicTypesWithSlots,
    ClinicTypesWithoutSlots
} from "../models";
import { loadAppointmentDetail, loadEntityAppointments } from "../actions";
import { AdminUser, loadAdminUsers } from "../../crud/adminUser";
import { routeDateSelector } from "../selectors";
import { Room, loadRooms } from "../../crud/room";
import { OhCaseState } from "../../crud/ohCase";
import { loadClinicTypes } from "../clinics/clinicTypes/actions";
import { loadClinicLocationTypes } from "../clinics/clinicLocationTypes/actions";
import { loadClinics, loadClinicsAndDistances } from "../clinics/actions";
import { Team } from "../../teams/model";
import { loadTeams } from "../../teams";
import {
    ClinicLocationType,
    ClinicType,
    ClinicDetails,
    ClinicAndDistanceResult,
    ClinicAndDistanceFailedGeocodeResult,
    ClinicAndDistance
} from "../clinics/models";
import { ADMIN_ROLES } from "../../auth";
import { CreateModal } from "../clinics/components/CreateModal";
import { getUserDisplayName } from "../../crud/adminUser/model";
import { loadRoomsAndDistances } from "../../crud/room/actions";
import { RoomAndDistanceResult, RoomAndDistanceFailedGeocodeResult, RoomAndDistance } from "../../crud/room/model";
import { CreateMobileClinicModal } from "../clinics/components/CreateMobileClinicModal";

import { Customer, loadCustomers } from "../../crud/customer";
import { ApplicationState } from "../../applicationState";

import { PostcodeAndDistanceSearch } from "./PostcodeAndDistanceSearch";

import { goBack } from "redux-little-router";

interface StateProps {
    path: string;
    startDate: moment.Moment;
    calendarDetails: ApplicationState["calendar"];
    clinics: ClinicDetails[];
    userId: string;
    adminUsers: AdminUser[];
    rooms: Room[];
    clinicTypes: ClinicType[];
    clinicLocationTypes: ClinicLocationType[];
    customers: Customer[];
    teams: Team[];
    isLocumUser?: boolean;
    dateOptions?: DropdownItemProps[];
    restrictToAppointmentId?: string;
}

interface OwnProps {
    showClinics?: boolean;
    roomSelector?: boolean;
    selectedRoomIds?: string[];
    onSelectedRoomIdsChanged?: (value: string[]) => void;
    teamPreventUserSelector?: boolean;
    selectorLimitedToTeamPreventUserIds?: string[];
    selectedUserIds?: string[];
    onSelectedUserIdsChanged?: (value: string[]) => void;
    highlight?: TimePeriod;
    showOnlyClinicsWithInsufficientClinicians?: boolean;
    onShowOnlyClinicsWithInsufficientCliniciansChanged?: (value: boolean) => void;
    focusDate?: moment.Moment;
    clinicType?: string;
    onClinicTypeChanged?: (value: string) => void;
    clinicLocationType?: ClinicLocationTypeName;
    onClinicLocationTypeChanged?: (value: string) => void;
    onCustomerChanged?: (value: string[]) => void;
    onEntitySelected?: (entityId: string, entityName: string) => void;
    onDateChange?: (date: moment.Moment, restrictToAppointmentId?: string) => void;
    onAppointmentClick?: (appointment: Appointment) => void;
    onAppointmentDrop?: (itemId: string, appointment: Appointment) => void;
    onHourDrop?: (itemId: string, hour: moment.Moment, entityId: string) => void;
    onHourClick?: (hour: moment.Moment, entityId: string) => void;
    customerIds?: string[];
    teamIds?: string[];
    professionsIds?: string[];
    onTeamChanged?: (value: string[]) => void;
    onProfessionsChanged?: (value: string[]) => void;
    showOnlyLocumUser?: boolean;
    onShowOnlyLocumUserChanged?: (value: boolean) => void;
    showAppointmentCount?: boolean;
    showAllUsersOption?: boolean;
    showAvailableAppointmentGaps?: boolean;
    onShowAvailableAppointmentGapsChanged?: (value: boolean) => void;
    hideAnyRowsWithNoClinics?: boolean;
    onHideAnyRowsWithNoClinicsChanged?: (value: boolean) => void;
    showGapsAndClinicsFilters?: boolean;
    minimumGapForFilter?: string;
    maximumGapForFilter?: string;
    onMinimumGapForFilterChanged?: (value: string) => void;
    onMaximumGapForFilterChanged?: (value: string) => void;
    onGapDurationChanged?: (value: string) => void;
    selectCurrentUserOnInit?: boolean;
}

interface DispatchProps {
    changeView: (
        userIds: string[],
        roomIds: string[],
        showClinics: boolean,
        showOnlyClinicsWithInsufficientClinicians: boolean,
        clinicType: string,
        clinicLocationType: string,
        startDate: moment.Moment,
        customerIds: string[],
        teamIds: string[],
        professionIds: string[],
        showOnlyLocumUser: boolean,
        showAvailableAppointmentGaps: boolean,
        hideAnyRowsWithNoClinics: boolean,
        minimumGapForFilter: string,
        view: CalendarView
    ) => void;
    loadAdminUsers: () => void;
    loadRooms: (name?: string) => Promise<Room[]>;
    loadClinicTypes: () => void;
    loadClinicLocationTypes: () => void;
    loadRoomsAndDistances: (postcode: string, distance: number) => Promise<RoomAndDistanceResult | RoomAndDistanceFailedGeocodeResult>;
    loadCustomers: () => Promise<void>;
    loadTeams: () => Promise<void>;
    loadClinicsAndDistances: (
        showOnlyClinicsWithInsufficientClinicians: boolean,
        clinicType: string,
        clinicLocationType: string,
        startDate: moment.Moment,
        view: CalendarView,
        postcode: string,
        distance: number) => Promise<ClinicAndDistanceResult | ClinicAndDistanceFailedGeocodeResult>;
    loadAppointmentDetail: (appointmentId: string) => Promise<Appointment>;
    goBack: () => void;
}

type AllProps = OwnProps & StateProps & DispatchProps;

interface State {
    selectedUserIds: string[];
    userIds: string[];
    selectedRoomIds: string[];
    roomIds: string[];
    showOnlyClinicsWithInsufficientClinicians: boolean;
    clinicType?: string;
    clinicLocationType?: ClinicLocationTypeName | "All";
    focusDate: moment.Moment;
    view: CalendarView;
    roomsAndDistances?: RoomAndDistance[];
    clinicsAndDistances?: ClinicAndDistance[];
    customerIds: string[];
    teamIds: string[];
    professionIds: string[];
    showOnlyLocumUser: boolean;
    clinicsLoadedForRestrictToAppointmentId?: boolean;
    showAvailableAppointmentGaps: boolean;
    hideAnyRowsWithNoClinics: boolean;
    minimumGapForFilter?: string;
}

export class CalendarComponent extends React.Component<AllProps, State> {
    private readonly allOptionValue = "All";
    private enableBookAppointmentsPageImprovements = false;

    constructor(props: AllProps) {
        super(props);
        const userIds = this.getInitialUserIds();
        this.onUsersChanged = this.onUsersChanged.bind(this);
        this.onRoomsChanged = this.onRoomsChanged.bind(this);
        this.onRoomSearch = this.onRoomSearch.bind(this);
        this.onViewChange = this.onViewChange.bind(this);
        this.onFocusDateChange = this.onFocusDateChange.bind(this);
        this.onChangeShowOnlyClinicsWithInsufficientClinicians = this.onChangeShowOnlyClinicsWithInsufficientClinicians.bind(this);
        this.onChangeShowLocumUserOnly = this.onChangeShowLocumUserOnly.bind(this);
        this.onClinicTypeChanged = this.onClinicTypeChanged.bind(this);
        this.onClinicLocationTypeChanged = this.onClinicLocationTypeChanged.bind(this);
        this.onRoomDistanceSearch = this.onRoomDistanceSearch.bind(this);
        this.onClinicDistanceSearch = this.onClinicDistanceSearch.bind(this);
        this.onCustomerChanged = this.onCustomerChanged.bind(this);
        this.onTeamChanged = this.onTeamChanged.bind(this);
        this.onProfessionsChanged = this.onProfessionsChanged.bind(this);
        this.customerExistsOnTeam = this.customerExistsOnTeam.bind(this);
        this.userExistsOnTeam = this.userExistsOnTeam.bind(this);
        this.professionExistsOnClinic = this.professionExistsOnClinic.bind(this);
        this.onProfessionsChangedLimitUsers = this.onProfessionsChangedLimitUsers.bind(this);
        this.onRefresh = this.onRefresh.bind(this);
        this.onChangeShowAvailableAppointmentGaps = this.onChangeShowAvailableAppointmentGaps.bind(this);
        this.onChangeHideAnyRowsWithNoClinics = this.onChangeHideAnyRowsWithNoClinics.bind(this);
        this.onMinimumGapForFilterChanged = this.onMinimumGapForFilterChanged.bind(this);

        const {
            showOnlyClinicsWithInsufficientClinicians,
            showOnlyLocumUser,
            clinicType,
            clinicLocationType,
            focusDate,
            selectedRoomIds,
            showAvailableAppointmentGaps,
            hideAnyRowsWithNoClinics
        } = props;

        this.enableBookAppointmentsPageImprovements = getFeatureToggles()[FeatureToggleNames.EnableBookAppointmentsPageImprovements];

        this.state = {
            userIds: props.selectedUserIds || userIds,
            selectedUserIds: this.getInitialSelectedUserId(props, userIds),
            roomIds: selectedRoomIds || [],
            selectedRoomIds: selectedRoomIds || [],
            showOnlyClinicsWithInsufficientClinicians:
                showOnlyClinicsWithInsufficientClinicians !== undefined ? showOnlyClinicsWithInsufficientClinicians : false,
            showOnlyLocumUser:
                showOnlyLocumUser !== undefined ? showOnlyLocumUser : false,
            focusDate: focusDate || props.startDate,
            clinicLocationType: clinicLocationType || this.allOptionValue,
            clinicType: clinicType || this.allOptionValue,
            customerIds: [],
            teamIds: [],
            professionIds: [],
            view: this.getDefaultView(),
            clinicsLoadedForRestrictToAppointmentId: false,
            showAvailableAppointmentGaps:
                showAvailableAppointmentGaps !== undefined ? showAvailableAppointmentGaps : false,
            hideAnyRowsWithNoClinics:
                hideAnyRowsWithNoClinics !== undefined ? hideAnyRowsWithNoClinics : false,
            minimumGapForFilter: GapDurations[1].gapInMinutes,
        };
        if (!props.adminUsers.length) {
            props.loadAdminUsers();
        }
        if (!props.rooms.length) {
            props.loadRooms();
        }
        if (!props.clinicTypes.length) {
            props.loadClinicTypes();
        }
        if (!props.clinicLocationTypes.length) {
            props.loadClinicLocationTypes();
        }
        if (!props.customers.length) {
            props.loadCustomers();
        }
        if (!props.teams.length) {
            props.loadTeams();
        }
    }

    private getInitialSelectedUserId(props: AllProps, userIds: string[]) {
        if (this.enableBookAppointmentsPageImprovements === false) {
            return (props.selectedUserIds?.length > 0) ? props.selectedUserIds : userIds;
        }

        if (props.selectedUserIds?.length > 0) {
            return props.selectedUserIds;
        }

        if (props.selectCurrentUserOnInit) {
            return userIds;
        }

        return [];
    }

    private getFilteredUsers() {
        const usersInSelectedTeams: string[] = [];

        const teamFilters = this.props.teams.filter((team) =>
            this.state.teamIds.indexOf(team.id) !== -1
        );

        teamFilters.forEach((team) =>
            team.members.concat(team.leaders)
                .forEach((members) =>
                    usersInSelectedTeams.push(members.id))
        );

        return this.props?.adminUsers?.filter((user) =>
            (this.state.professionIds.length === 0 || this.state.professionIds.includes(user.profession))
                && (this.state.teamIds.length === 0 || usersInSelectedTeams.includes(user.id))
                && (this.state.showOnlyLocumUser ? user.isLocumUser : true)
        );
    }

    private getFilteredUserOptions() {
        const userOptions = this.getFilteredUsers()
            .map(adminUser =>
                ({ key: adminUser.id, value: adminUser.id, text: getUserDisplayName(adminUser) }));

        if (!this.props.showClinics && this.props.showAllUsersOption) {
            return [{ key: "All", text: "All", value: this.allOptionValue }, ...userOptions];
        }

        return userOptions;
    }

    private getFilteredOrAllUserIds(userIds: string[]) {
        if (!userIds) {
            return [];
        }

        return !this.props.showClinics && userIds.indexOf(this.allOptionValue) > -1
            ? this.getFilteredUsers().map(u => u.id)
            : userIds.filter(u => u !== this.allOptionValue);
    }

    private changeEntities = debounce((userIds: string[], roomIds: string[]) => {
        this.setState({ userIds, roomIds, clinicsLoadedForRestrictToAppointmentId: true });
        userIds = this.getFilteredOrAllUserIds(userIds);
        this.props.changeView(
            userIds,
            roomIds,
            this.props.showClinics,
            this.state.showOnlyClinicsWithInsufficientClinicians,
            this.state.clinicType === this.allOptionValue ? null : this.state.clinicType,
            this.state.clinicLocationType === this.allOptionValue ? null : this.state.clinicLocationType,
            this.state.focusDate.clone().startOf(this.state.view),
            this.state.customerIds,
            this.state.teamIds,
            this.state.professionIds,
            this.state.showOnlyLocumUser,
            this.state.showAvailableAppointmentGaps,
            this.state.hideAnyRowsWithNoClinics,
            this.state.minimumGapForFilter,
            this.state.view
        );
    }, debounceDelay);

    private onFocusDateChange = debounce((start: moment.Moment) => {
        this.setState({ focusDate: start });
        if (this.props.onDateChange) {
            this.props.onDateChange(start, this.props.restrictToAppointmentId);
        }
        this.changeView(start, this.state.view);
    }, debounceDelay);

    private onViewChange = debounce((view: CalendarView) => {
        this.setState({ view });
        this.changeView(this.state.focusDate, view);
    }, debounceDelay);

    private changeView(start: moment.Moment, view: CalendarView) {
        const userIds = this.getFilteredOrAllUserIds(this.state.userIds);

        this.props.changeView(
            userIds,
            this.state.roomIds,
            this.props.showClinics,
            this.state.showOnlyClinicsWithInsufficientClinicians,
            this.state.clinicType === this.allOptionValue ? null : this.state.clinicType,
            this.state.clinicLocationType === this.allOptionValue ? null : this.state.clinicLocationType,
            start.clone().startOf(view),
            this.state.customerIds,
            this.state.teamIds,
            this.state.professionIds,
            this.state.showOnlyLocumUser,
            this.state.showAvailableAppointmentGaps,
            this.state.hideAnyRowsWithNoClinics,
            this.state.minimumGapForFilter,
            view
        );
    }

    private getInitialUserIds() {
        const userIds = !this.props.showClinics && !this.props.roomSelector && !this.props.teamPreventUserSelector &&
            this.props.userId && this.props.adminUsers.find(u => u.id === this.props.userId) ? [this.props.userId] : [];
        return userIds;
    }

    public customerExistsOnTeam(teamIds: string[], clinic: ClinicDetails) {
        const teamFilters = this.props.teams.filter((team) => {
            return teamIds.indexOf(team.id) !== -1;
        });

        const customerIds: string[] = [];
        teamFilters.forEach((team) => {
            team.customers
                .forEach((customer) => {
                    customerIds.push(customer.id);
                });
        });

        return clinic.customerIds.filter((c: string) => customerIds.includes(c)).length > 0
            || (clinic.customer && customerIds.includes(clinic.customer.id)); // For mobile clinics
    }

    public userExistsOnTeam(teamIds: string[], clinic: ClinicDetails) {
        const teamFilters = this.props.teams.filter((team) => {
            return teamIds.indexOf(team.id) !== -1;
        });

        const userIds: string[] = [];
        teamFilters.forEach((team) => {
            team.leaders.concat(team.members)
                .forEach((user) => {
                    userIds.push(user.id);
                });
        });

        return clinic.teamPreventUsers.filter(u => userIds.includes(u.id)).length > 0;
    }

    public showOnlyLocumUsersFilter(clinic: ClinicDetails) {

        const locumAdminUsers = this.props.adminUsers
            .filter(a => a.isLocumUser)
            .map(u => u.id);

        return clinic.teamPreventUsers.filter(u => locumAdminUsers.includes(u.id)).length > 0;
    }

    public filterForAppointmentGaps(clinics: CalendarDetails[]): CalendarDetails[] {

        if (!this.state.showAvailableAppointmentGaps) {
            return clinics;
        }

        return clinics.map(c => {

            const slotsWithGaps = c.appointments.map(a => {
                // For clinic types without slots, gaps are calculated differently
                if (ClinicTypesWithoutSlots.indexOf(a.clinicType) >= 0) {
                    return isNonSlotClinicAvailable(
                        a,
                        this.state.minimumGapForFilter && Number(this.state.minimumGapForFilter) ? Number(this.state.minimumGapForFilter) : null);
                }

                if (ClinicTypesWithSlots.indexOf(a.clinicType) >= 0) {
                    return filterAppointsBasedOnGapLength(a,
                        this.state.minimumGapForFilter && Number(this.state.minimumGapForFilter) ? Number(this.state.minimumGapForFilter) : null);
                }

                return false;
            });

            const hasAnySlotGaps = slotsWithGaps.some(s => s);

            return { ...c, hasAnySlotGaps };
        })
            .filter(c => c.hasAnySlotGaps || !c.entityId);
    }

    public filterForClinics(clinics: CalendarDetails[]): CalendarDetails[] {

        if (this.props.showClinics || !this.state.hideAnyRowsWithNoClinics) {
            return clinics;
        }

        return clinics
            .filter(c => c.hasAnyClinics || !c.entityId);
    }

    public sortCalendarDetails(clinics: CalendarDetails[]): CalendarDetails[] {

        if (this.props.showClinics || this.state.selectedUserIds.indexOf(this.allOptionValue) < 0) {
            return clinics;
        }

        return clinics
            .sort((a, b) => (b.hasAnyClinics === true ? 1 : 0) - (a.hasAnyClinics === true ? 1 : 0));
    }

    public professionExistsOnClinic(professionIds: string[], clinic: ClinicDetails) {

        const clinicUserIds: string[] = clinic.teamPreventUsers.map((user) => {
            return user.id;
        });

        const validUsers = this.props.adminUsers.filter((user) => {
            return !!(clinicUserIds.includes(user.id) && professionIds.includes(user.profession));
        });

        return validUsers.length > 0;
    }

    public render(): React.JSX.Element {
        const defaultCalendarDetails: CalendarDetails = {
            appointments: emptyCalendarDetails.appointments,
            workingHours: null,
            entityName: "",
            entityId: "",
            hasAnySlotGaps: false,
            hasAnyClinics: false
        };

        const filteredUserIds = this.getFilteredOrAllUserIds(this.state.userIds);

        const focusStartDate = this.state.focusDate.clone().startOf("day");
        const focusEndDate = this.state.focusDate.clone().endOf("day");

        const userCalendarDetails = this.props.showClinics ? [] : filteredUserIds.map(userId => {
            const user = this.props.adminUsers.find(adminUser => adminUser.id === userId);
            const entityName = (user && getUserDisplayName(user));
            const { appointments, ...workingHours } = this.props.calendarDetails[userId] || emptyCalendarDetails;
            return {
                appointments,
                entityName,
                entityId: userId,
                workingHours,
                hasAnySlotGaps: false,
                hasAnyClinics: appointments?.some(a =>
                    focusStartDate.isBetween(a.start, a.end, null, "[]") ||
                        focusEndDate.isBetween(a.start, a.end, null, "[]") ||
                        a.start.isBetween(focusStartDate, focusEndDate, null, "[]") ||
                        a.end.isBetween(focusStartDate, focusEndDate, null, "[]"))
            };
        });

        const roomCalendarDetails = this.props.showClinics ? [] : this.state.roomIds.map(roomId => {
            const room = this.props.rooms.find(r => r.id === roomId);
            const roomAndDistance = this.state.roomsAndDistances && this.state.roomsAndDistances.find(r => r.id === roomId);
            const entityName = (room && room.name);
            const entitySubHeading = roomAndDistance && `(${roomAndDistance.distance.toFixed(2)} miles)`;
            const { appointments, ...workingHours } = this.props.calendarDetails[roomId] || emptyCalendarDetails;
            return {
                appointments,
                entityName,
                entityId: roomId,
                workingHours,
                entitySubHeading,
                distance: roomAndDistance && roomAndDistance.distance,
                hasAnySlotGaps: false,
                hasAnyClinics: false
            };
        }).sort((a, b) => (a.distance >= 0 ? a.distance : Number.MAX_SAFE_INTEGER) - (b.distance >= 0 ? b.distance : Number.MAX_SAFE_INTEGER));

        const startOfView = this.state.focusDate.clone().startOf(this.state.view);
        const endOfView = this.state.focusDate.clone().endOf(this.state.view);

        let clinicDetails: CalendarDetails[];

        if (this.props.showClinics) {
            let filteredClinics: (ClinicDetails & { distance?: number })[];
            if (this.state.clinicsAndDistances) {
                const clinicIds = this.state.clinicsAndDistances.map(c => c.id);
                filteredClinics = this.props.clinics
                    .filter(c => clinicIds.indexOf(c.id) > -1)
                    .map(c => ({ ...c, distance: this.state.clinicsAndDistances.find(cd => cd.id === c.id).distance }))
                    .sort((a, b) => (a.distance >= 0 ? a.distance : Number.MAX_SAFE_INTEGER) - (b.distance >= 0 ? b.distance : Number.MAX_SAFE_INTEGER));
            } else {
                filteredClinics = this.props.clinics
                    .filter(clinic =>
                        (clinic.start.isBetween(startOfView, endOfView) ||
                            clinic.end.isBetween(startOfView, endOfView)) &&
                        (!this.state.showOnlyClinicsWithInsufficientClinicians || clinic.teamPreventUsers.length < clinic.cliniciansRequired) &&
                        (this.state.clinicType === this.allOptionValue || clinic.clinicType === this.state.clinicType) &&
                        (this.state.clinicLocationType === this.allOptionValue || clinic.clinicLocationTypes.indexOf(this.state.clinicLocationType) !== -1) &&
                        (this.state.customerIds.length === 0 ||
                            clinic.customerIds.some(c => c === this.allOptionValue) ||
                            clinic.customerIds.filter((c) => this.state.customerIds.includes(c))?.length > 0 ||
                            (clinic.customer && this.state.customerIds.includes(clinic.customer.id))) && // For mobile clinics
                        (this.state.teamIds.length === 0 ||
                            this.userExistsOnTeam(this.state.teamIds, clinic)) &&
                        (this.state.professionIds.length === 0 || this.professionExistsOnClinic(this.state.professionIds, clinic)) &&
                        (!this.state.showOnlyLocumUser || this.showOnlyLocumUsersFilter(clinic))
                    );
            }

            clinicDetails = filteredClinics.map(clinic => {
                const description = [
                    (clinic.rooms || []).map(r => r.name).join(", "),
                    clinic.teamPreventUsers.map(u => u.name).join(", "),
                    clinic.clinicLocationTypes.join(", "),
                    clinic.clinicType,
                    clinic.customer && clinic.customer.name
                ].filter(i => !!i).join("\n");
                return {
                    entityName: description,
                    entitySubHeading: clinic.distance ? `(${clinic.distance.toFixed(2)} miles)` : null,
                    entityId: clinic.id,
                    appointments: [clinic],
                    workingHours: {
                        workingDays: emptyCalendarDetails.workingDays,
                        workingDayStartTime: emptyCalendarDetails.workingDayStartTime,
                        workingDayEndTime: emptyCalendarDetails.workingDayEndTime
                    }
                };
            });
        } else {
            clinicDetails = [];
        }
        const calendarDetails = clinicDetails.length + userCalendarDetails.length + roomCalendarDetails.length ?
            [...userCalendarDetails, ...roomCalendarDetails, ...clinicDetails]
            : [defaultCalendarDetails];

        const filteredUserOptions = this.getFilteredUserOptions();

        const sortedAndFilteredCalendarDetails = this.sortCalendarDetails(
            this.filterForClinics(
                this.filterForAppointmentGaps(calendarDetails)));

        return (
            <div className="clinician-calendar">
                <BaseCalendar
                    view={this.state.view}
                    details={sortedAndFilteredCalendarDetails}
                    startDate={this.props.focusDate || this.props.startDate}
                    highlight={this.props.highlight}
                    onViewChange={this.onViewChange}
                    onFocusDateChange={this.onFocusDateChange}
                    onEntitySelected={this.props.onEntitySelected}
                    onAppointmentClick={this.props.onAppointmentClick}
                    onAppointmentDrop={this.props.onAppointmentDrop}
                    onHourDrop={this.props.onHourDrop}
                    onHourClick={this.props.onHourClick}
                    showDateRange={this.props.isLocumUser}
                    dateOptions={this.props.dateOptions}
                    restrictToAppointmentId={this.props.restrictToAppointmentId}
                    showAppointmentCount={this.props.showAppointmentCount}
                    goBack={this.props.goBack}
                >
                    {!this.props.restrictToAppointmentId &&
                        <Grid>
                            <Grid.Row>
                                {this.props.showClinics ?
                                    this.renderClinicsFilters() :
                                    this.renderNonClinicsFilters(filteredUserOptions)
                                }
                            </Grid.Row>
                        </Grid>
                    }
                </BaseCalendar>
            </div>
        );
    }

    public componentDidUpdate(prevProps: AllProps) {
        if (this.props.restrictToAppointmentId) {
            if (!this.state.clinicsLoadedForRestrictToAppointmentId) {
                this.setState({ clinicsLoadedForRestrictToAppointmentId: true });
                this.onRestrictToAppointmentIdChanged();
            }
        }
        else {
            if (this.enableBookAppointmentsPageImprovements)
            {
                if (this.props.selectCurrentUserOnInit) {
                    if (prevProps.userId !== this.props.userId && this.state.userIds.length === 0 && !this.props.showClinics) {
                        this.onUsersChanged([this.props.userId]);
                    }

                    const adminUsersLoaded = prevProps.adminUsers?.length !== this.props.adminUsers?.length;
                    if (adminUsersLoaded && (!this.state.selectedUserIds || this.state.selectedUserIds.length === 0)) {
                        const userIds = this.getInitialUserIds();
                        this.onUsersChanged(userIds);
                    }
                }
            }
            else
            {
                if (prevProps.userId !== this.props.userId && this.state.userIds.length === 0 && !this.props.showClinics) {
                    this.onUsersChanged([this.props.userId]);
                }

                const adminUsersLoaded = prevProps.adminUsers?.length !== this.props.adminUsers?.length;
                if (adminUsersLoaded && (!this.state.selectedUserIds || this.state.selectedUserIds.length === 0)) {
                    const userIds = this.getInitialUserIds();
                    this.onUsersChanged(userIds);
                }
            }
        }

        if (!prevProps.startDate.isSame(this.props.startDate)) {
            this.setState({ focusDate: this.props.startDate });
        }

        if (prevProps.calendarDetails &&
            this.props.calendarDetails &&
            this.hasAnyAppointmentChanged(prevProps.calendarDetails, this.props.calendarDetails)) {
            this.onAppointmentsChanged();
        }
    }

    public async onRoomDistanceSearch(postcode: string, distance: number) {
        this.setState({ roomsAndDistances: null });
        const roomsAndDistancesResult = await this.props.loadRoomsAndDistances(postcode, distance);
        if (roomsAndDistancesResult) {
            if (roomsAndDistancesResult.geocodedSuccessful) {
                const roomIds = roomsAndDistancesResult.roomsAndDistances.map(r => r.id);
                this.setState({ selectedRoomIds: roomIds, roomIds, roomsAndDistances: roomsAndDistancesResult.roomsAndDistances });
                const userIds = this.getFilteredOrAllUserIds(this.state.userIds);

                this.props.changeView(
                    userIds,
                    roomIds,
                    this.props.showClinics,
                    this.state.showOnlyClinicsWithInsufficientClinicians,
                    this.state.clinicType === this.allOptionValue ? null : this.state.clinicType,
                    this.state.clinicLocationType === this.allOptionValue ? null : this.state.clinicLocationType,
                    this.state.focusDate,
                    this.state.customerIds,
                    this.state.teamIds,
                    this.state.professionIds,
                    this.state.showOnlyLocumUser,
                    this.state.showAvailableAppointmentGaps,
                    this.state.hideAnyRowsWithNoClinics,
                    this.state.minimumGapForFilter,
                    this.state.view
                );
            } else {
                toast.error(`Unable to locate the postcode '${postcode}'`);
            }
        }
    }

    public async onClinicDistanceSearch(postcode: string, distance: number) {
        this.setState({ clinicsAndDistances: null });
        const clinicsAndDistancesResult = await this.props.loadClinicsAndDistances(
            this.state.showOnlyClinicsWithInsufficientClinicians,
            this.state.clinicType === this.allOptionValue ? null : this.state.clinicType,
            this.state.clinicLocationType === this.allOptionValue ? null : this.state.clinicLocationType,
            this.state.focusDate,
            this.state.view,
            postcode,
            distance);
        if (clinicsAndDistancesResult) {
            if (clinicsAndDistancesResult.geocodedSuccessful) {
                this.setState({ clinicsAndDistances: clinicsAndDistancesResult.clinicsAndDistances });
            } else {
                toast.error(`Unable to locate the postcode '${postcode}'`);
            }
        }
    }

    private getDefaultView(): CalendarView {
        return "day";
    }

    private async onRoomSearch(name: string): Promise<DropdownItemProps[]> {
        if (name.length === 0) {
            return [];
        }

        const rooms = await this.props.loadRooms(name);
        return rooms.map(room => ({ key: room.id, text: room.name, value: room.id }));
    }

    private onUsersChanged(userIds: string[]) {
        userIds = userIds.indexOf(this.allOptionValue) > -1 ? [this.allOptionValue] : userIds;
        this.setState({ selectedUserIds: userIds, clinicsLoadedForRestrictToAppointmentId: true });

        const previousFilterHadAllOption = this.state.userIds.indexOf(this.allOptionValue) > -1;
        const currentFilterHasAllOption = userIds.indexOf(this.allOptionValue) > -1;

        if (!currentFilterHasAllOption ||
            !previousFilterHadAllOption) {
            this.changeEntities(userIds, this.state.roomIds);
            if (this.props.onSelectedUserIdsChanged) {
                this.props.onSelectedUserIdsChanged(userIds);
            }
        }
    }

    private onRoomsChanged(roomIds: string[]) {
        this.setState({ selectedRoomIds: roomIds });
        this.changeEntities(this.state.userIds, roomIds);
        if (this.props.onSelectedRoomIdsChanged) {
            this.props.onSelectedRoomIdsChanged(roomIds);
        }
    }

    private onChangeShowOnlyClinicsWithInsufficientClinicians(value: boolean) {
        if (value !== this.state.showOnlyClinicsWithInsufficientClinicians) {
            this.setState({ showOnlyClinicsWithInsufficientClinicians: value });
            this.changeEntities(this.state.userIds, this.state.roomIds);
            if (this.props.onShowOnlyClinicsWithInsufficientCliniciansChanged) {
                this.props.onShowOnlyClinicsWithInsufficientCliniciansChanged(value);
            }
        }
    }

    private onChangeShowLocumUserOnly(value: boolean) {
        if (value !== this.state.showOnlyLocumUser) {
            this.setState({ showOnlyLocumUser: value, selectedUserIds: [] });
            this.changeEntities([], this.state.roomIds);

            if (this.props.onShowOnlyLocumUserChanged) {
                this.props.onShowOnlyLocumUserChanged(value);
            }
        }
    }

    private onChangeShowAvailableAppointmentGaps(value: boolean) {
        if (value !== this.state.showAvailableAppointmentGaps) {
            this.setState({ showAvailableAppointmentGaps: value });
            this.changeEntities(this.state.userIds, this.state.roomIds);
            if (this.props.onShowAvailableAppointmentGapsChanged) {
                this.props.onShowAvailableAppointmentGapsChanged(value);
            }
        }
    }

    private onChangeHideAnyRowsWithNoClinics(value: boolean) {
        if (value !== this.state.hideAnyRowsWithNoClinics) {
            this.setState({ hideAnyRowsWithNoClinics: value });
            this.changeEntities(this.state.userIds, this.state.roomIds);
            if (this.props.onHideAnyRowsWithNoClinicsChanged) {
                this.props.onHideAnyRowsWithNoClinicsChanged(value);
            }
        }
    }

    private onRefresh() {
        this.changeEntities(this.state.userIds, this.state.roomIds);
    }

    private onClinicTypeChanged(value: string) {
        this.setState({ clinicType: value });
        this.changeEntities(this.state.userIds, this.state.roomIds);
        if (this.props.onClinicLocationTypeChanged) {
            this.props.onClinicTypeChanged(value);
        }
    }

    private onCustomerChanged(value: string[]) {
        this.setState({ customerIds: value });
        this.changeEntities(this.state.userIds, this.state.roomIds);
        if (this.props.onCustomerChanged) {
            this.props.onCustomerChanged(value);
        }
    }

    private onTeamChanged(value: string[]) {
        // This is to make sure we reset the selected users ONLY when the user makes a change to the team
        // and not when the page loads, for example when closing an open appointment
        if (this.state.teamIds.length !== value.length) {
            this.setState({ teamIds: value, selectedUserIds: [] });
            this.changeEntities([], this.state.roomIds);
        }

        if (this.props.onTeamChanged) {
            this.props.onTeamChanged(value);
        }
    }

    private onProfessionsChanged(value: string[]) {
        // This is to make sure we reset the selected users ONLY when the user makes a change to the professions
        // and not when the page loads, for example when closing an open appointment
        if (this.state.professionIds.length !== value.length) {
            this.setState({ professionIds: value, selectedUserIds: [] });
            this.changeEntities([], this.state.roomIds);
        }

        if (this.props.onProfessionsChanged) {
            this.props.onProfessionsChanged(value);
        }
    }

    private onProfessionsChangedLimitUsers(value: string[]) {
        this.setState({ professionIds: value });

        this.changeEntities(this.state.userIds, this.state.roomIds);
        if (this.props.onProfessionsChanged) {
            this.props.onProfessionsChanged(value);
        }
    }

    private onClinicLocationTypeChanged(value: ClinicLocationTypeName) {
        this.setState({ clinicLocationType: value });
        this.changeEntities(this.state.userIds, this.state.roomIds);
        if (this.props.onClinicLocationTypeChanged) {
            this.props.onClinicLocationTypeChanged(value);
        }
    }

    private onMinimumGapForFilterChanged(value: string) {
        if (!this.state.showAvailableAppointmentGaps) {
            return;
        }

        this.setState({ minimumGapForFilter: value });
        this.changeEntities(this.state.userIds, this.state.roomIds);
        if (this.props.onGapDurationChanged) {
            this.props.onGapDurationChanged(value);
        }
    }

    private hasAnyAppointmentChanged(prevValue: ApplicationState["calendar"], newValue: ApplicationState["calendar"]): boolean {

        const prevCalendar = transform<ApplicationState["calendar"], AdminCalendarDetails[]>(prevValue, (result, value, entityId) => {
            result.push({
                ...value,
                entityId
            });
        }, []);
        const newCalendar = transform<ApplicationState["calendar"], AdminCalendarDetails[]>(newValue, (result, value, entityId) => {
            result.push({
                ...value,
                entityId
            });
        }, []);

        if (prevCalendar === newCalendar ||
            (prevCalendar.length === 0 &&
                newCalendar.length === 0)) {
            return false;
        }

        if (prevCalendar.length !== newCalendar.length) {
            return true;
        }

        return prevCalendar.some(prevCalendarValue => {
            const newCalendarValue = newCalendar.find(c => c.entityId === prevCalendarValue.entityId);

            if (!newCalendarValue) {
                return true;
            }

            if (prevCalendarValue.appointments.length !== newCalendarValue.appointments.length) {
                return true;
            }

            if (prevCalendarValue.appointments.some(prevCalendarAppointmentValue => {
                const newCalendarAppointmentValue =
                    newCalendarValue.appointments.find(c => c.id === prevCalendarAppointmentValue.id);

                if (!newCalendarValue) {
                    return true;
                }

                if (prevCalendarAppointmentValue.appointments.length !==
                    newCalendarAppointmentValue.appointments.length) {
                    return true;
                }

                return false;
            })) {
                return true;
            }

            return false;
        });
    }

    private onAppointmentsChanged() {
        this.changeEntities(this.state.userIds, this.state.roomIds);
    }

    private async onRestrictToAppointmentIdChanged() {
        this.setState({ clinicsLoadedForRestrictToAppointmentId: true });
        const appointment = await this.props.loadAppointmentDetail(this.props.restrictToAppointmentId);
        this.onUsersChanged(appointment.teamPreventUsers.map(a => a.id));
    }

    private renderClinicsFilters() {
        return (
            <>
                <CreateModal />
                <CreateMobileClinicModal />
                <Grid.Column className="filters">
                    <Form>
                        <Grid columns="3" stackable>
                            <Grid.Column>
                                <Input.Dropdown
                                    dynamicOptions
                                    label="Clinic Type"
                                    options={[{ key: "All", text: "All", value: this.allOptionValue }, ...this.props.clinicTypes.map(clinicType =>
                                        ({ key: clinicType.name, value: clinicType.name, text: clinicType.name }))]}
                                    onChange={this.onClinicTypeChanged}
                                    value={this.state.clinicType}
                                />
                                <Input.Dropdown
                                    dynamicOptions
                                    label="Clinic Location Type"
                                    /* eslint-disable max-len */
                                    options={[{ key: "All", text: "All", value: this.allOptionValue }, ...this.props.clinicLocationTypes.map(clinicLocationType =>
                                        ({ key: clinicLocationType.name, value: clinicLocationType.name, text: clinicLocationType.name }))]}
                                    onChange={this.onClinicLocationTypeChanged}
                                    value={this.state.clinicLocationType}
                                />
                                <Input.Checkbox
                                    label="Show only insufficient clinicians assigned"
                                    onChange={this.onChangeShowOnlyClinicsWithInsufficientClinicians}
                                    value={this.state.showOnlyClinicsWithInsufficientClinicians}
                                />
                                <Input.Checkbox
                                    label="Show only locum users"
                                    onChange={this.onChangeShowLocumUserOnly}
                                    value={this.state.showOnlyLocumUser}
                                />
                                {this.props.showGapsAndClinicsFilters &&
                                <>
                                    <Input.Checkbox
                                        label="Show available appointment gaps"
                                        onChange={this.onChangeShowAvailableAppointmentGaps}
                                        value={this.state.showAvailableAppointmentGaps}
                                    />
                                    {this.state.showAvailableAppointmentGaps &&
                                    <>
                                        <Input.Dropdown
                                            dynamicOptions
                                            label="Minimum Duration"
                                            options={GapDurations.map(gap =>
                                                ({ key: gap.gapInMinutes, value: gap.gapInMinutes, text: gap.text }))}
                                            onChange={this.onMinimumGapForFilterChanged}
                                            value={this.state.minimumGapForFilter}
                                        />
                                    </>
                                    }
                                </>
                                }
                            </Grid.Column>

                            <Grid.Column>
                                <Input.DropdownMulti
                                    dynamicOptions
                                    value={this.state.customerIds}
                                    label="Customers"
                                    onChange={this.onCustomerChanged}
                                    options={this.props.customers.map(customer =>
                                        ({ key: customer.id, value: customer.id, text: customer.name }))}
                                    search
                                />
                                <Input.DropdownMulti
                                    dynamicOptions
                                    value={this.state.teamIds}
                                    label="Teams"
                                    onChange={this.onTeamChanged}
                                    options={this.props.teams.map(team =>
                                        ({ key: team.id, value: team.id, text: team.name }))}
                                    search
                                />
                                <Input.DropdownMulti
                                    value={this.state.professionIds}
                                    label="Professions"
                                    options={getProfessionDropdownOptions()}
                                    onChange={this.onProfessionsChanged}
                                />
                            </Grid.Column>

                            <Grid.Column>
                                <PostcodeAndDistanceSearch
                                    onSearch={this.onClinicDistanceSearch}
                                    buttonText="Show clinics"
                                />
                            </Grid.Column>

                        </Grid>
                    </Form>
                </Grid.Column>
            </>
        );
    }

    private renderNonClinicsFilters(filteredUserOptions: DropdownItemProps[]) {
        return (
            <Grid.Column className="filters">
                <Form>
                    <Grid columns="3" stackable>
                        <Grid.Column>
                            {!this.props.roomSelector &&
                                <>
                                    <Input.DropdownMulti
                                        dynamicOptions
                                        value={this.state.selectedUserIds}
                                        label="Users"
                                        onChange={this.onUsersChanged}
                                        options={filteredUserOptions}
                                        search
                                    />
                                </>
                            }
                            {!this.props.teamPreventUserSelector &&
                                <div className="rooms-dropdown">
                                    <Input.DropdownMulti
                                        dynamicOptions
                                        value={this.state.selectedRoomIds}
                                        label="Rooms"
                                        onChange={this.onRoomsChanged}
                                        options={this.props.rooms.map(room => ({ key: room.id, value: room.id, text: room.name }))}
                                        search={this.onRoomSearch}
                                        placeholder="Start typing a room name..."
                                        debounce={200}
                                    />
                                </div>
                            }

                            {this.props.showGapsAndClinicsFilters &&
                            <>
                                <Input.Checkbox
                                    label="Hide any rows with no Clinics"
                                    onChange={this.onChangeHideAnyRowsWithNoClinics}
                                    value={this.state.hideAnyRowsWithNoClinics}
                                />
                                <Input.Checkbox
                                    label="Show available appointment gaps"
                                    onChange={this.onChangeShowAvailableAppointmentGaps}
                                    value={this.state.showAvailableAppointmentGaps}
                                />
                                {this.state.showAvailableAppointmentGaps &&
                                    <>
                                        <Input.Dropdown
                                            dynamicOptions
                                            label="Minimum Duration"
                                            options={GapDurations.map(gap =>
                                                ({ key: gap.gapInMinutes, value: gap.gapInMinutes, text: gap.text }))}
                                            onChange={this.onMinimumGapForFilterChanged}
                                            value={this.state.minimumGapForFilter}
                                        />
                                    </>
                                }
                                <Input.Checkbox
                                    label="Show only locum users"
                                    onChange={this.onChangeShowLocumUserOnly}
                                    value={this.state.showOnlyLocumUser}
                                />
                            </>
                            }
                        </Grid.Column>
                        <Grid.Column>
                            <Input.DropdownMulti
                                dynamicOptions
                                value={this.state.customerIds}
                                label="Customers"
                                onChange={this.onCustomerChanged}
                                options={this.props.customers.map(customer =>
                                    ({ key: customer.id, value: customer.id, text: customer.name }))}
                                search
                            />
                            <Input.DropdownMulti
                                dynamicOptions
                                value={this.state.teamIds}
                                label="Teams"
                                onChange={this.onTeamChanged}
                                options={this.props.teams.map(team =>
                                    ({ key: team.id, value: team.id, text: team.name }))}
                                search
                            />
                            <Input.DropdownMulti
                                value={this.state.professionIds}
                                label="Professions"
                                options={getProfessionDropdownOptions()}
                                onChange={this.onProfessionsChanged}
                            />
                        </Grid.Column>
                        <Grid.Column>
                            {!this.props.teamPreventUserSelector &&
                                <PostcodeAndDistanceSearch onSearch={this.onRoomDistanceSearch} buttonText="Show rooms" />
                            }
                            <Button icon onClick={this.onRefresh}>
                                <Icon name='refresh' />
                            </Button>
                        </Grid.Column>
                    </Grid>
                </Form>
            </Grid.Column>
        );
    }
}

const mapStateToProps = (state: ApplicationState & OhCaseState, ownProps: OwnProps): StateProps => {
    const path = state.router.pathname;
    const calendarDetails = state.calendar;
    const clinics = state.clinic.clinics.sort((a, b) => a.start.diff(b.start));
    const startDate = routeDateSelector(state);
    const adminUsers = state.adminUsers
        .filter(user => user.roles.indexOf(ADMIN_ROLES.CLINICIAN.name) !== -1)
        .filter(user => !ownProps.selectorLimitedToTeamPreventUserIds || ownProps.selectorLimitedToTeamPreventUserIds.includes(user.id))
        .filter(user => !state.currentUser.isLocumUser || user.id === state.currentUser.id)
        .sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)));
    const rooms = state.rooms.sort((a, b) => a.name.localeCompare(b.name));
    const { clinicTypes, clinicLocationTypes, customers, teams } = state;
    const highlightStart = ownProps.highlight && ownProps.highlight.start && ownProps.highlight.start.clone();
    const dateOptions = getDateOptionsForLocumUser(state.currentUser.isLocumUser);
    const restrictToAppointmentId = state.router.query.RestrictToAppointmentId;

    return {
        path,
        calendarDetails,
        clinics,
        userId: state.currentUser && state.currentUser.id,
        adminUsers,
        rooms,
        startDate: highlightStart || startDate,
        clinicTypes,
        clinicLocationTypes,
        customers,
        teams,
        isLocumUser: state.currentUser.isLocumUser,
        dateOptions,
        restrictToAppointmentId
    };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => {
    return {
        changeView: (
            userIds,
            roomIds,
            showClinics,
            showOnlyClinicsWithInsufficientClinicians,
            clinicType,
            clinicLocationType,
            start,
            customerIds,
            teamIds,
            professionIds,
            showOnlyLocumUser,
            showAvailableAppointmentGaps,
            hideAnyRowsWithNoClinics,
            minimumGapForFilter,
            view
        ) => {
            dispatch(loadEntityAppointments(
                userIds,
                roomIds,
                start,
                view));
            if (showClinics) {
                dispatch(loadClinics(
                    showOnlyClinicsWithInsufficientClinicians,
                    clinicType,
                    clinicLocationType,
                    start,
                    teamIds,
                    customerIds,
                    professionIds,
                    view));
            }
        },
        loadAdminUsers: () => dispatch(loadAdminUsers()),
        loadRooms: (name?: string) => dispatch(loadRooms({ name })),
        loadClinicTypes: () => dispatch(loadClinicTypes()),
        loadClinicLocationTypes: () => dispatch(loadClinicLocationTypes()),
        loadCustomers: () => dispatch(loadCustomers()),
        loadTeams: () => dispatch(loadTeams()),
        loadRoomsAndDistances: (postcode: string, distance: number) => dispatch(loadRoomsAndDistances(postcode, distance)),
        loadClinicsAndDistances: (
            showOnlyClinicsWithInsufficientClinicians: boolean,
            clinicType: string,
            clinicLocationType: string,
            startDate: moment.Moment,
            view: CalendarView,
            postcode: string,
            distance: number) =>
            dispatch(loadClinicsAndDistances(showOnlyClinicsWithInsufficientClinicians, clinicType, clinicLocationType, startDate, view, postcode, distance)),
        loadAppointmentDetail: (appointmentId: string) => dispatch(loadAppointmentDetail({ appointmentId })),
        goBack: () => dispatch(goBack())
    };
};

export const Calendar = connect(mapStateToProps, mapDispatchToProps)(CalendarComponent);
/* eslint-enable max-len */
